import { useMemo, useState } from "react";
import { capitalize } from "lodash";
import { MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { ArrowDropDownRounded } from "@mui/icons-material";
import TextLink from "../TextLink";
import { getAvailableSizes, getSizeAndFrame } from "../../helpers/product";
import FrameMockUp from "../FrameMockUp";
import { CollectionProductFragment, useGetAttributeProductsQuery } from "../../generated/storefront";
import { Frame, Size, frames } from "../../types/product";
import { formattedPrice } from "../../helpers/money";
import { colors, fonts } from "../../theme";
import FrameBadge from "../FrameBadges/FrameBadge";
import { useAppState } from "../../state";
import { FrameType } from "../../helpers/basket";

type Props = {
  product: CollectionProductFragment;
  setSelectedVariant: (productId: string, variantId: string, frame: string) => void;
  defaultFrame: Frame;
  lastChild: boolean;
};

const CollectionProductItem = ({ product, setSelectedVariant, defaultFrame, lastChild }: Props) => {
  const { selectedCountry } = useAppState();
  const [selectedFrame, setSelectedFrame] = useState(`${defaultFrame}-${FrameType.Standard}`);
  const [selectedSize, setSelectedSize] = useState(getAvailableSizes(product.variants.nodes)[0]);
  const { data: attributeProductsData } = useGetAttributeProductsQuery({
    variables: { country: selectedCountry },
  });
  const { standardFrameProduct, premiumFrameProduct } = attributeProductsData || {};
  const selectedVariant = useMemo(() => {
    return product.variants.nodes.find((variant) => {
      const { size, frame } = getSizeAndFrame(variant.selectedOptions);
      return size === selectedSize && frame === Frame.Unframed;
    });
  }, [product.variants.nodes, selectedSize]);
  const selectedFrameColour = useMemo(() => selectedFrame.split("-")[0] as Frame, [selectedFrame]);

  const onFrameChange = ({ target }: SelectChangeEvent) => {
    setSelectedFrame(target.value as Frame);
    if (selectedVariant) {
      setSelectedVariant(product.id, selectedVariant.id, target.value as Frame);
    }
  };

  const onSizeChange = ({ target }: SelectChangeEvent) => {
    setSelectedSize(target.value as Size);
    const selectedVariant = product.variants.nodes.find((variant) => {
      const { size, frame } = getSizeAndFrame(variant.selectedOptions);
      return size === target.value && frame === Frame.Unframed;
    });
    if (selectedVariant) {
      setSelectedVariant(product.id, selectedVariant.id, selectedFrame);
    }
  };
  const frameColors = frames.filter((frame) => frame !== Frame.Unframed);

  const framePrice = useMemo(() => {
    if (selectedFrame === Frame.Unframed) return 0;
    const frameProduct = selectedFrame.split("-")[1] === FrameType.Standard ? standardFrameProduct : premiumFrameProduct;
    return Number(
      frameProduct?.variants.nodes.find((variant) => variant.selectedOptions[0].value === selectedSize)?.price.amount || 0
    );
  }, [selectedSize, selectedFrame, standardFrameProduct, premiumFrameProduct]);

  return (
    <Stack
      key={product.id}
      direction="row"
      justifyContent="space-between"
      borderBottom={lastChild ? 0 : `1px solid ${colors.grey10}`}
      paddingX={{ xs: 2, md: 5 }}
      paddingBottom={2}
      gap={2}
    >
      <Stack gap={2}>
        <Stack gap={0.5}>
          <TextLink to={`/products/${product.handle}`}>
            <Typography>{product.title}</Typography>
          </TextLink>
          <TextLink to={`/artists/${product.vendor}`}>
            <Typography fontSize={14}>{product.artistName?.value || ""}</Typography>
          </TextLink>
        </Stack>
        <Stack direction="row" gap={1}>
          <Select
            variant="filled"
            onChange={onFrameChange}
            value={selectedFrame}
            IconComponent={ArrowDropDownRounded}
            sx={{ paddingRight: 1 }}
          >
            <MenuItem key={Frame.Unframed} value={Frame.Unframed}>
              <Typography fontSize={12} fontFamily={fonts.body} marginRight={0.6}>
                Unframed
              </Typography>
              <FrameBadge frame={Frame.Unframed} size={14} />
            </MenuItem>
            {frameColors.map((frame) => (
              <MenuItem key={frame} value={`${frame}-${FrameType.Standard}`}>
                <Typography fontSize={12} fontFamily={fonts.body} marginRight={0.6}>
                  {capitalize(FrameType.Standard)} Frame - {capitalize(frame)}
                </Typography>
                <FrameBadge frame={frame} size={14} />
              </MenuItem>
            ))}
            {frameColors.map((frame) => (
              <MenuItem key={frame} value={`${frame}-${FrameType.Premium}`}>
                <Typography fontSize={12} fontFamily={fonts.body} marginRight={0.6}>
                  {capitalize(FrameType.Premium)} Frame - {capitalize(frame)}
                </Typography>
                <FrameBadge frame={frame} size={14} />
              </MenuItem>
            ))}
          </Select>
          <Select variant="filled" onChange={onSizeChange} value={selectedSize} IconComponent={ArrowDropDownRounded}>
            {getAvailableSizes(product.variants.nodes).map((frame) => (
              <MenuItem key={frame} value={frame}>
                <Typography fontSize={12} fontFamily={fonts.body} lineHeight="100%">
                  {capitalize(frame)}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </Stack>
        {selectedVariant && (
          <Typography fontSize={14}>
            {formattedPrice(Number(selectedVariant.priceV2.amount) + framePrice, selectedVariant.priceV2.currencyCode)}
          </Typography>
        )}
      </Stack>
      <Stack flexBasis="18%">
        <FrameMockUp image={product.images.nodes[0]} frame={selectedFrameColour} />
      </Stack>
    </Stack>
  );
};
export default CollectionProductItem;
